import { useEffect, useState } from 'react';

/**
 * Track the OS-level `prefers-reduced-motion: reduce` setting and re-render
 * when the user changes their preference live (Settings panel, system theme
 * sync, etc.). SSR-safe: defaults to `false` when `window` or `matchMedia`
 * is undefined.
 *
 * Consumers use the boolean to swap motion-heavy code paths for instant
 * equivalents — see TourProvider (scrollIntoView behavior) and TourCard
 * (width transition class) for the canonical call sites.
 */
export const usePrefersReducedMotion = (): boolean => {
  const [prefers, setPrefers] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handler = (e: MediaQueryListEvent) => setPrefers(e.matches);
    // Modern API. Older Safari (<14) used addListener; we rely on the project
    // staying on evergreen browsers, which the rest of the codebase also
    // assumes (see waitForElement's `Element.checkVisibility()` decision).
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  return prefers;
};
